import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { CalendarRange } from 'lucide-react'
import { formatCompactMoney } from '@/lib/format'
import type { KpiTrends } from '@/types/analytics'
import {
  CHART_AXIS_TICK,
  CHART_GRID_PROPS,
  CHART_TOOLTIP_PROPS,
} from '@/features/dashboard/components/chart-theme'
import { monthLabel } from './chart-utils'

interface RevenuePlanChartProps {
  trends: KpiTrends | undefined
  isLoading: boolean
}

// Revenue vs. plan: actual ledger revenue against the monthly budget.
export function RevenuePlanChart({ trends, isLoading }: RevenuePlanChartProps) {
  const chartData =
    trends?.revenue_vs_budget.map((p) => ({
      month: monthLabel(p.month),
      Actual: Math.round(p.actual),
      Budget: Math.round(p.budget),
    })) ?? []

  return (
    <Card className="lg:col-span-2">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <CalendarRange className="h-4 w-4" /> Revenue vs. Plan
        </CardTitle>
        <CardDescription>Actual revenue against monthly budget, trailing 12 months</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-[240px] w-full" />
        ) : chartData.length === 0 ? (
          <div className="text-muted-foreground flex h-[240px] items-center justify-center text-sm">
            No revenue data
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={chartData}>
              <CartesianGrid {...CHART_GRID_PROPS} />
              <XAxis dataKey="month" tick={CHART_AXIS_TICK} axisLine={false} tickLine={false} />
              <YAxis
                tick={CHART_AXIS_TICK}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v: number) => formatCompactMoney(v)}
              />
              <Tooltip {...CHART_TOOLTIP_PROPS} formatter={(v) => formatCompactMoney(Number(v))} />
              <Legend
                verticalAlign="bottom"
                iconType="rect"
                iconSize={8}
                formatter={(value: string) => (
                  <span className="text-xs text-muted-foreground">{value}</span>
                )}
              />
              <Bar dataKey="Actual" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Budget" fill="hsl(var(--muted))" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  )
}
